import type { ChangeEvent } from 'react';
import React, { useMemo, useState } from 'react';

import {
  useGetDepartment,
  useGetPermissionTags,
  usePatchDepartment,
} from '@vlabs/api/artemy/client';
import type { PermissionTag } from '@vlabs/api/artemy/model/PermissionTag';
import { DropdownMenu } from '@vlabs/uikit';
import { toast } from 'react-toastify';

import { useAttachDepartmentPermissionTagsTranslation } from '../i18n';
import type { AttachDepartmentPermissionTagsProps } from '../types';

const POPUP_WIDTH = 280;
const INITIAL_PAGE_SIZE = 10;

interface AttachDepartmentPermissionTagsDropdownProps extends AttachDepartmentPermissionTagsProps {
  children: React.ReactElement;
  disabled?: boolean;
}

function AttachDepartmentPermissionTagsDropdown({
  children,
  departmentId,
  disabled = false,
}: AttachDepartmentPermissionTagsDropdownProps): React.JSX.Element {
  const { t } = useAttachDepartmentPermissionTagsTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);

  const getTags = useGetPermissionTags(
    {
      permission_tags: query || undefined,
      page_size: pageSize,
    },
    { query: { enabled: isOpen } },
  );

  const getDepartment = useGetDepartment(departmentId, {
    query: { enabled: isOpen },
  });

  const patchDepartment = usePatchDepartment({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        setIsOpen(false);
      },
      onError: () => {
        toast.error(t('on_error'));
      },
    },
  });

  const isLoading = isOpen && (getTags.isLoading || getDepartment.isLoading);
  const hasError = getTags.isError || getDepartment.isError;
  const isDisabled = disabled || patchDepartment.isPending;
  const hasMore = (getTags.data?.permission_tags?.length ?? 0) === pageSize;

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      setQuery('');
      setPageSize(INITIAL_PAGE_SIZE);
    }
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
    setPageSize(INITIAL_PAGE_SIZE);
  };

  const handleAttachTag = (permissionTag: PermissionTag): void => {
    const currentTags = getDepartment.data?.permission_tags ?? [];
    const newTags = [
      ...currentTags,
      { permission_tag: permissionTag },
    ];
    patchDepartment.mutate({ departmentId, data: { permission_tags: newTags } });
  };

  const availableOptions = useMemo(() => {
    if (!getTags.data?.permission_tags || !getDepartment.data) return [];
    const attachedTags = getDepartment.data.permission_tags ?? [];
    return getTags.data.permission_tags
      .filter((tag) => !attachedTags.some((item) => item.permission_tag === tag.permission_tag));
  }, [getTags.data, getDepartment.data]);

  return (
    <DropdownMenu.Root
      modal={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <DropdownMenu.Trigger
        disabled={isDisabled}
        render={children}
      />
      <DropdownMenu.Portal>
        <DropdownMenu.Positioner>
          <DropdownMenu.Popup style={{ width: POPUP_WIDTH }}>
            <DropdownMenu.SearchItem
              onChange={handleQueryChange}
              placeholder={t('search_placeholder') as unknown as string}
              size="small"
              value={query}
            />
            {isLoading ? (
              <DropdownMenu.EmptyItem>{t('loading')}</DropdownMenu.EmptyItem>
            ) : null}
            {!isLoading && hasError ? (
              <DropdownMenu.EmptyItem>{t('load_error')}</DropdownMenu.EmptyItem>
            ) : null}
            {!isLoading && !hasError && availableOptions.length === 0 ? (
              <DropdownMenu.EmptyItem>{t('empty')}</DropdownMenu.EmptyItem>
            ) : null}
            {!isLoading && !hasError ? availableOptions.map((tag) => (
              <DropdownMenu.Item
                disabled={patchDepartment.isPending}
                key={tag.permission_tag}
                onClick={() => handleAttachTag(tag.permission_tag)}
              >
                {tag.permission_tag}
              </DropdownMenu.Item>
            )) : null}
            {!isLoading && !hasError && hasMore ? (
              <DropdownMenu.Item
                closeOnClick={false}
                onClick={() => setPageSize((prev) => prev + INITIAL_PAGE_SIZE)}
              >
                {t('load_more')}
              </DropdownMenu.Item>
            ) : null}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export { AttachDepartmentPermissionTagsDropdown };
